import React, { useState, useEffect, useCallback } from 'react';
import { Box, CircularProgress, Alert, Snackbar } from '@mui/material';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import { isConfigurationMobileLayout, getConfigurationEmbeddedRootSx } from '../utils/configurationMobileLayout';
import { useAmenities } from '../hooks/useAmenities';
import { useSnackbar } from '../hooks/useSnackbar';
import { CategoryCard } from '../components/amenities/CategoryCard';
import { AmenityDrawer } from '../components/amenities/AmenityDrawer';
import { 
  AMENITIES_BY_CATEGORY, 
  amenityNameToCode, 
  createTempAmenityId, 
  isTempId 
} from '../constants/amenities';
import { 
  fetchAmenitiesByCategory, 
  createAmenity, 
  updateAmenity 
} from '../services/amenityService';

const AmenitiesPage = ({ showHeader = true }) => {
  const isMobileLayout = isConfigurationMobileLayout();
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerCategory, setDrawerCategory] = useState(null);
  const [drawerAmenities, setDrawerAmenities] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [saving, setSaving] = useState(false);

  const {
    categories,
    setCategories,
    selectedAmenities,
    loading,
    fetchAmenities,
    toggleAmenity,
    removeAmenity,
    setCategorySelectedAmenities,
  } = useAmenities();

  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  useEffect(() => {
    fetchAmenities().catch(() => {
      showError('Failed to load amenities. Please try again.');
    });
  }, [fetchAmenities, showError]);

  const isDark = currentTheme === 'dark';

  /**
   * Prepare amenities list for drawer
   */
  const prepareAmenitiesForDrawer = useCallback((amenityList, existingAmenities) => {
    return amenityList.map((name, index) => {
      const existing = existingAmenities.find(
        e => e.name.toLowerCase() === name.toLowerCase()
      );
      return {
        id: existing ? existing.id : createTempAmenityId(drawerCategory?.value || '', index),
        name: name,
        code: amenityNameToCode(name),
        isExisting: !!existing,
      };
    });
  }, [drawerCategory]);

  /**
   * Handle opening drawer for a category
   */
  const handleOpenDrawer = useCallback(async (category) => {
    setDrawerCategory(category);
    setDrawerOpen(true);
    
    const amenityList = AMENITIES_BY_CATEGORY[category.value] || [];
    
    try {
      const response = await fetchAmenitiesByCategory(category.value);
      const existingAmenities = response?.categories?.[0]?.amenities || [];
      
      const amenitiesWithIds = prepareAmenitiesForDrawer(amenityList, existingAmenities);
      setDrawerAmenities(amenitiesWithIds);
      
      // Set selected amenities to those that exist in the database
      const selectedIds = amenitiesWithIds
        .filter(a => a.isExisting)
        .map(a => a.id);
      
      setCategorySelectedAmenities(category.value, selectedIds);
    } catch (error) {// Fallback: just show the comprehensive list
      const amenitiesWithIds = amenityList.map((name, index) => ({
        id: createTempAmenityId(category.value, index),
        name: name,
        code: amenityNameToCode(name),
        isExisting: false,
      }));
      
      setDrawerAmenities(amenitiesWithIds);
      setCategorySelectedAmenities(category.value, []);
    }
  }, [prepareAmenitiesForDrawer, setCategorySelectedAmenities]);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setDrawerCategory(null);
    setDrawerAmenities([]);
  }, []);

  const handleToggleAmenityInDrawer = useCallback((amenityId) => {
    if (!drawerCategory) return;
    toggleAmenity(drawerCategory.value, amenityId);
  }, [drawerCategory, toggleAmenity]);

  const handleRemoveAmenity = useCallback((categoryValue, amenityId) => {
    removeAmenity(categoryValue, amenityId);
  }, [removeAmenity]);

  /**
   * Save selected amenities
   */
  const handleSave = useCallback(async () => {
    if (!drawerCategory) return;

    setSaving(true);
    try {
      const selectedIds = selectedAmenities[drawerCategory.value] || [];
      const selectedAmenitiesList = drawerAmenities.filter(a => selectedIds.includes(a.id));
      
      // Fetch existing amenities to check what already exists
      const existingResponse = await fetchAmenitiesByCategory(drawerCategory.value);
      const existingAmenities = existingResponse?.categories?.[0]?.amenities || [];

      // Save each selected amenity
      const savePromises = selectedAmenitiesList.map(amenity => {
        if (!isTempId(amenity.id)) {
          // Update existing amenity
          return updateAmenity(amenity.id, {
            name: amenity.name,
            code: amenity.code,
            category: drawerCategory.value,
            is_active: true,
          }).catch(error => {return Promise.resolve();
          });
        } else {
          // Check if amenity with same name already exists
          const existing = existingAmenities.find(
            e => e.name.toLowerCase() === amenity.name.toLowerCase()
          );
          if (existing) {
            // Update existing amenity
            return updateAmenity(existing.id, {
              name: amenity.name,
              code: amenity.code,
              category: drawerCategory.value,
              is_active: true,
            }).catch(error => {return Promise.resolve();
            });
          } else {
            // Create new amenity
            return createAmenity({
              name: amenity.name,
              code: amenity.code,
              category: drawerCategory.value,
              is_active: true,
            }).catch(error => {
              if (error.response?.status === 400) {}
              return Promise.resolve();
            });
          }
        }
      });

      await Promise.all(savePromises);
      
      // Update the category state
      const category = categories.find(c => c.value === drawerCategory.value);
      if (category) {
        category.selectedCount = selectedIds.length;
        category.amenities = selectedAmenitiesList.map(a => ({
          id: a.id,
          name: a.name,
          code: a.code,
        }));
        setCategories([...categories]);
      }

      showSuccess('Amenities saved successfully');
      
      // Refresh the amenities list
      await fetchAmenities();
      handleCloseDrawer();
    } catch (error) {showError('Failed to save amenities. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [
    drawerCategory,
    drawerAmenities,
    selectedAmenities,
    categories,
    setCategories,
    fetchAmenities,
    handleCloseDrawer,
    showSuccess,
    showError,
  ]);

  const handleToggleCategory = useCallback((categoryValue) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryValue]: !prev[categoryValue],
    }));
  }, []);

  const getSelectedAmenitiesForCategory = useCallback((categoryValue) => {
    const selectedIds = selectedAmenities[categoryValue] || [];
    const category = categories.find(c => c.value === categoryValue);
    if (!category) return [];
    
    return category.amenities.filter(a => selectedIds.includes(a.id));
  }, [categories, selectedAmenities]);

  return (
    <Box sx={{ p: showHeader ? 3 : 0, ...getConfigurationEmbeddedRootSx(isMobileLayout) }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {categories.map((category) => {
            const isExpanded = expandedCategories[category.value];
            const selectedAmenitiesList = getSelectedAmenitiesForCategory(category.value);
            const count = selectedAmenitiesList.length;

            return (
              <CategoryCard
                key={category.value}
                category={category}
                isExpanded={isExpanded}
                selectedCount={count}
                selectedAmenities={selectedAmenitiesList}
                isDark={isDark}
                onToggleExpand={() => handleToggleCategory(category.value)}
                onOpenDrawer={() => handleOpenDrawer(category)}
                onRemoveAmenity={(amenityId) => handleRemoveAmenity(category.value, amenityId)}
              />
            );
          })}
        </Box>
      )}

      {/* Amenity Drawer */}
      <AmenityDrawer
        open={drawerOpen}
        category={drawerCategory}
        amenities={drawerAmenities}
        selectedAmenityIds={selectedAmenities[drawerCategory?.value] || []}
        isDark={isDark}
        saving={saving}
        onClose={handleCloseDrawer}
        onToggleAmenity={handleToggleAmenityInDrawer}
        onSave={handleSave}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={hideSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AmenitiesPage;
